import type { Ref } from 'react';
import type { Message, Participant, SessionInfo } from './types';
import { SessionTranscriptMessageList } from './SessionTranscriptMessageList';

const TRANSCRIPT_SUBTITLE = 'Conversation messages from this session.';

export function SessionTranscriptPanel({
  headingId,
  messages,
  sessionInfo,
  participants,
  user,
  loading,
  messagesEndRef,
  rootClassName,
  showMobileHideButton,
  onHideTranscript,
}: {
  headingId: string;
  messages: Message[];
  sessionInfo: SessionInfo;
  participants: Participant[];
  user: { id?: string; name?: string | null } | null;
  loading?: boolean;
  messagesEndRef?: Ref<HTMLDivElement | null>;
  /** Merged with default transcript column layout (aside width, max-height on mobile). */
  rootClassName?: string;
  showMobileHideButton?: boolean;
  onHideTranscript?: () => void;
}) {
  const defaultRoot =
    'w-full md:w-[min(100%,26rem)] md:shrink-0 flex flex-col min-h-0 max-h-[min(50dvh,24rem)] md:max-h-none border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden';

  return (
    <aside className={rootClassName ?? defaultRoot} role="region" aria-labelledby={headingId}>
      <div className="shrink-0 px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div>
          <h2 id={headingId} className="text-sm font-semibold text-gray-900">
            Transcript
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">{TRANSCRIPT_SUBTITLE}</p>
        </div>
        {showMobileHideButton && onHideTranscript ? (
          <button
            type="button"
            onClick={onHideTranscript}
            className="md:hidden p-1 text-gray-500 hover:text-gray-800"
            aria-label="Hide transcript"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : null}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        <SessionTranscriptMessageList
          messages={messages}
          sessionInfo={sessionInfo}
          participants={participants}
          user={user}
          loading={loading}
          messagesEndRef={messagesEndRef}
        />
      </div>
    </aside>
  );
}
